import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const { getCurrentBrand } = require('../../config/brands');

const SEO = ({ pageName, customDescription, customKeywords }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language;
  const brand = getCurrentBrand();
  
  // Get environment variables with fallbacks to ensure we always have values
  const getEnvVar = (key, fallback) => {
    if (window.ENV && window.ENV[key]) return window.ENV[key];
    if (process.env[key]) return process.env[key];
    return fallback;
  };
  
  // Ensure we have the brand data regardless of how environment variables are loaded
  const brandName = getEnvVar('REACT_APP_BRAND_NAME', brand.name);
  const metaTitle = getEnvVar('REACT_APP_META_TITLE', brand.metaTitle);
  const metaDescription = getEnvVar('REACT_APP_META_DESCRIPTION', brand.metaDescription);
  const metaKeywords = getEnvVar('REACT_APP_META_KEYWORDS', brand.metaKeywords);
  const themeColor = getEnvVar('REACT_APP_THEME_COLOR', brand.themeColor);
  const domain = getEnvVar('REACT_APP_DOMAIN', brand.domain);
  const brandId = getEnvVar('REACT_APP_BRAND', 'ezseat');
  
  // Use absolute paths for favicons with cache-busting and a random ID to ensure uniqueness
  const uniqueId = Math.random().toString(36).substring(2, 8);
  const favicon = `/favicon.ico?v=${uniqueId}`;
  const favicon16 = `/favicon-16x16.webp?v=${uniqueId}`;
  const favicon32 = `/favicon-32x32.webp?v=${uniqueId}`;
  const appleTouchIcon = `/apple-touch-icon.webp?v=${uniqueId}`;
  const safariPinnedTab = `/safari-pinned-tab.svg?v=${uniqueId}`;
  
  // Logo path
  const logoPath = `/images/brands/${brandId}_logo.webp`;
  
  // Memoize derived values
  const title = useMemo(() => `${brandName} - ${metaTitle}`, [brandName, metaTitle]);
  const description = useMemo(() => customDescription || metaDescription, [customDescription, metaDescription]);
  const keywords = useMemo(() => customKeywords || metaKeywords, [customKeywords, metaKeywords]);
  
  // URL construction based on the domain
  const protocol = domain.includes('azurestaticapps.net') ? 'https://' : 'https://www.';
  const baseUrl = `${protocol}${domain}`;
  const fullUrl = useMemo(() => `${baseUrl}${location.pathname}`, [baseUrl, location.pathname]);
  const imageUrl = useMemo(() => `${window.location.origin}${logoPath}`, [logoPath]);

  // Function to handle favicon updates - we'll call this in multiple places
  const updateFavicons = useCallback(() => {
    // Check if favicons are already updated with our version
    const currentVersion = document.querySelector('link[rel="icon"][data-version]')?.dataset?.version;
    if (currentVersion === uniqueId) {
      return; // Skip if already updated with current version
    }

    // IMPORTANT: Force remove any existing favicon links first 
    document.querySelectorAll('link[rel*="icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"], link[rel="shortcut icon"], link[rel="manifest"]')
      .forEach(link => {
        console.log(`Removing old favicon link: ${link.href}`);
        link.parentNode.removeChild(link);
      });
    
    // Helper function to create and append link elements with relative paths
    const updateLink = (rel, href, attributes = {}) => {
      // Create a new link element
      const link = document.createElement('link');
      link.rel = rel;
      
      // Always ensure we use relative paths by removing the domain if it exists
      const currentOrigin = window.location.origin;
      const relativeHref = href.startsWith(currentOrigin) 
        ? href.substring(currentOrigin.length)
        : href.startsWith('http') 
          ? new URL(href).pathname 
          : href;
          
      link.href = relativeHref;
      
      // Add version attribute to track updates
      link.dataset.version = uniqueId;
      
      // Add attributes
      Object.entries(attributes).forEach(([key, value]) => {
        link.setAttribute(key, value);
      });
      
      document.head.appendChild(link);
      console.log(`Added ${rel} link: ${relativeHref}`);
    };
    
    // Add new favicon links with correct relative paths
    updateLink('shortcut icon', favicon);
    updateLink('icon', favicon, { type: 'image/x-icon' });
    updateLink('icon', favicon16, { sizes: '16x16', type: 'image/webp' });
    updateLink('icon', favicon32, { sizes: '32x32', type: 'image/webp' });
    updateLink('apple-touch-icon', appleTouchIcon, { sizes: '180x180' });
    updateLink('mask-icon', safariPinnedTab, { color: themeColor });
    
    // Add manifest link with proper path
    updateLink('manifest', '/manifest.json');
    
  }, [favicon, favicon16, favicon32, appleTouchIcon, safariPinnedTab, themeColor, uniqueId]);

  useEffect(() => {
    // Force update document title
    document.title = title;
    
    // Update all meta tags
    const metaTags = {
      // Basic meta tags
      'title': title,
      'description': description,
      'keywords': keywords,
      'theme-color': themeColor,
      'application-name': brandName,
      'apple-mobile-web-app-title': brandName,
      
      // OpenGraph tags
      'og:type': 'website',
      'og:url': fullUrl,
      'og:title': title,
      'og:description': description,
      'og:image': imageUrl,
      
      // Twitter card tags
      'twitter:card': 'summary_large_image',
      'twitter:url': fullUrl,
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': imageUrl,
      
      // Other meta tags
      'msapplication-TileColor': themeColor
    };
    
    // Update each meta tag
    Object.entries(metaTags).forEach(([key, value]) => {
      let elements = document.querySelectorAll(`meta[name="${key}"], meta[property="${key}"]`);
      
      // If elements exist, update them
      if (elements.length > 0) {
        elements.forEach(element => {
          element.content = value;
        });
      } 
      // If not, create new meta tags
      else {
        const meta = document.createElement('meta');
        if (key.startsWith('og:') || key.startsWith('twitter:')) {
          meta.setAttribute('property', key);
        } else {
          meta.setAttribute('name', key);
        }
        meta.content = value;
        document.head.appendChild(meta);
      }
    });
    
    // Update favicons only once
    updateFavicons();
    
    // No need for multiple updates since we now track versions
    
    // Update canonical and sitemap links
    const updateLink = (rel, href, attributes = {}) => {
      let link = document.querySelector(`link[rel="${rel}"]${attributes.sizes ? `[sizes="${attributes.sizes}"]` : ''}`);
      
      if (link) {
        link.href = href;
        // Update other attributes
        Object.entries(attributes).forEach(([key, value]) => {
          if (key !== 'href') link.setAttribute(key, value);
        });
      } else {
        // Create a new link if it doesn't exist
        link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        
        // Add attributes
        Object.entries(attributes).forEach(([key, value]) => {
          link.setAttribute(key, value);
        });
        
        document.head.appendChild(link);
      }
    };
    
    updateLink('canonical', fullUrl);
    updateLink('sitemap', `${baseUrl}/sitemap.xml`, { type: 'application/xml' });
    
    // Add schema.org JSON-LD
    const schemaScript = document.getElementById('bus-service-schema');
    if (schemaScript) {
      document.head.removeChild(schemaScript);
    }
    
    // Create Bus Travel Service Schema
    const busServiceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Bus Ticket Booking Service",
      "provider": {
        "@type": "Organization",
        "name": brandName,
        "url": baseUrl
      },
      "name": title,
      "description": description,
      "areaServed": {
        "@type": "Country",
        "name": "Thailand"
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": "https://booking.ezseat.asia",
        "providedBy": {
          "@type": "Organization",
          "name": brandName
        }
      }
    };
    
    // Create and add script element with Bus Service schema
    const script = document.createElement('script');
    script.id = 'bus-service-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(busServiceSchema);
    document.head.appendChild(script);
    
    // SEO debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('SEO Update:', {
        title,
        description,
        keywords,
        path: location.pathname,
        brand: brandName,
        lang: currentLang,
        faviconPaths: {
          favicon,
          favicon16,
          favicon32,
          appleTouchIcon,
          safariPinnedTab
        },
        logoPath
      });
    }
    
    return () => {
      // No need to remove event listener since we're not using it anymore
    };
  }, [
    title, description, keywords, fullUrl, imageUrl, 
    brandName, metaTitle, themeColor, 
    baseUrl, location.pathname, currentLang, domain, logoPath,
    favicon, favicon16, favicon32, appleTouchIcon, safariPinnedTab,
    updateFavicons // Added missing dependency
  ]);
  
  return null;
};

export default SEO;